import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, AdminUser } from '../../api/admin';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../i18n';
import { PencilButton, TrashButton } from '../../components/IconButton';

type SortKey = 'name' | 'email' | 'level' | 'role' | 'is_active' | 'created_at';
type SortDir = 'asc' | 'desc';

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  return (
    <span className={`ml-1 inline-block transition-colors ${active ? 'text-primary-600' : 'text-gray-300'}`}>
      {active && dir === 'desc' ? '↓' : '↑'}
    </span>
  );
}

export default function AdminUsers() {
  const [search, setSearch] = useState('');
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [editRole, setEditRole] = useState<'player' | 'admin'>('player');
  const [editActive, setEditActive] = useState(true);
  const [deleteUser, setDeleteUser] = useState<AdminUser | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const { t } = useTranslation();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin-users', search],
    queryFn: () => adminApi.getUsers(search || undefined).then((r) => r.data),
  });

  const updateMutation = useMutation({
    mutationFn: () => adminApi.updateUser(editUser!.id, { role: editRole, is_active: editActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setEditUser(null);
      setError('');
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail ?? t('admin.error'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setDeleteUser(null);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail ?? t('admin.error'));
    },
  });

  function openEdit(user: AdminUser) {
    setEditUser(user);
    setEditRole(user.role);
    setEditActive(user.is_active);
    setError('');
  }

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const sortedUsers = useMemo(() => {
    return [...(users as AdminUser[])].sort((a, b) => {
      let aVal: string | number | boolean;
      let bVal: string | number | boolean;
      switch (sortKey) {
        case 'name':
          aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
          bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        case 'email':
          aVal = a.email.toLowerCase();
          bVal = b.email.toLowerCase();
          break;
        case 'level':
          aVal = (a.level ?? '').toLowerCase();
          bVal = (b.level ?? '').toLowerCase();
          break;
        case 'role':
          aVal = a.role;
          bVal = b.role;
          break;
        case 'is_active':
          aVal = a.is_active ? 1 : 0;
          bVal = b.is_active ? 1 : 0;
          break;
        case 'created_at':
          aVal = a.created_at;
          bVal = b.created_at;
          break;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, sortKey, sortDir]);

  function thClass(key: SortKey) {
    return `text-left py-2 pr-4 font-medium cursor-pointer select-none hover:text-primary-600 transition-colors ${
      sortKey === key ? 'text-primary-600' : 'text-gray-500'
    }`;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin" className="text-primary-600 hover:text-primary-700 text-sm">{t('admin.backToAdmin')}</Link>
        <span className="text-gray-300">/</span>
        <h1 className="text-xl font-bold text-gray-900">{t('admin.usersTitle')}</h1>
      </div>

      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-sm"
          placeholder={t('admin.userSearch')}
        />
      </div>

      {/* Edit modal */}
      {editUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('admin.editUserTitle', { name: `${editUser.first_name} ${editUser.last_name}` })}
            </h3>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
                {error}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="label">{t('admin.roleField')}</label>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as 'player' | 'admin')}
                  className="input"
                >
                  <option value="player">{t('admin.rolePlayer')}</option>
                  <option value="admin">{t('admin.roleAdmin')}</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="active-checkbox"
                  checked={editActive}
                  onChange={(e) => setEditActive(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                />
                <label htmlFor="active-checkbox" className="text-sm text-gray-700">
                  {t('admin.accountActive')}
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditUser(null)} className="btn btn-secondary">{t('admin.cancel')}</button>
              <button
                onClick={() => updateMutation.mutate()}
                disabled={updateMutation.isPending}
                className="btn btn-primary"
              >
                {t('admin.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Benutzer löschen</h3>
            <p className="text-sm text-gray-600 mb-6">
              Soll <strong>{deleteUser.first_name} {deleteUser.last_name}</strong> ({deleteUser.email}) wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button onClick={() => { setDeleteUser(null); setError(''); }} className="btn btn-secondary">
                {t('admin.cancel')}
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteUser.id)}
                disabled={deleteMutation.isPending}
                className="btn btn-danger"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users table */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse h-14" />
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className={thClass('name')} onClick={() => handleSort('name')}>
                    {t('admin.colName')}<SortIcon active={sortKey === 'name'} dir={sortDir} />
                  </th>
                  <th className={thClass('email')} onClick={() => handleSort('email')}>
                    {t('admin.colEmail')}<SortIcon active={sortKey === 'email'} dir={sortDir} />
                  </th>
                  <th className={thClass('level')} onClick={() => handleSort('level')}>
                    {t('admin.colLevel')}<SortIcon active={sortKey === 'level'} dir={sortDir} />
                  </th>
                  <th className={thClass('role')} onClick={() => handleSort('role')}>
                    {t('admin.colRole')}<SortIcon active={sortKey === 'role'} dir={sortDir} />
                  </th>
                  <th className={thClass('is_active')} onClick={() => handleSort('is_active')}>
                    {t('admin.colStatus')}<SortIcon active={sortKey === 'is_active'} dir={sortDir} />
                  </th>
                  <th className={thClass('created_at')} onClick={() => handleSort('created_at')}>
                    {t('admin.colSince')}<SortIcon active={sortKey === 'created_at'} dir={sortDir} />
                  </th>
                  <th className="py-2 text-gray-500 font-medium">{t('admin.colAction')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => (
                  <tr key={user.id} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 pr-4 font-medium text-gray-900">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="py-2 pr-4 text-gray-500">{user.email}</td>
                    <td className="py-2 pr-4 text-gray-500">{user.level ?? '–'}</td>
                    <td className="py-2 pr-4">
                      <span className={`badge ${
                        user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {user.role === 'admin' ? t('admin.userRoleAdmin') : t('admin.userRolePlayer')}
                      </span>
                    </td>
                    <td className="py-2 pr-4">
                      <span className={`badge ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {user.is_active ? t('admin.userStatusActive') : t('admin.userStatusDeactivated')}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-gray-500">
                      {new Date(user.created_at).toLocaleDateString(getDateLocale())}
                    </td>
                    <td className="py-2">
                      <div className="flex items-center gap-1">
                        <PencilButton onClick={() => openEdit(user)} />
                        <TrashButton onClick={() => { setDeleteUser(user); setError(''); }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sortedUsers.length === 0 && (
              <p className="text-center text-gray-500 py-8">{t('admin.noUsers')}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
